/**
 * Security Tab Component
 * Handles session management and device management
 */

import React, { useState } from 'react';
import { Card, Heading, Text, Button, Badge, Spinner, Icon, Alert } from '../ui/index.jsx';
import {
    useSecuritySessions,
    useRevokeSession,
    useRevokeAllSessions,
    useSecurityDevices,
    useRemoveDevice
} from '../../hooks/queries/useSecurity.js';

// Helper to format relative time
function formatRelativeTime(dateString) {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
}

// Get device icon based on type
function getDeviceIcon(type) {
    switch (type?.toLowerCase()) {
        case 'mobile': return 'phone';
        case 'tablet': return 'tablet';
        case 'desktop':
        default: return 'monitor';
    }
}

export function SecurityTab() {
    const [showRevokeAllConfirm, setShowRevokeAllConfirm] = useState(false);
    const [revokingSessionId, setRevokingSessionId] = useState(null);
    const [removingDeviceId, setRemovingDeviceId] = useState(null);

    // Query hooks
    const { data: sessionsData, isLoading: sessionsLoading, refetch: refetchSessions } = useSecuritySessions();
    const { data: devicesData, isLoading: devicesLoading, refetch: refetchDevices } = useSecurityDevices();

    // Mutation hooks
    const revokeSession = useRevokeSession({
        onSuccess: () => {
            window.showToast?.('Session revoked successfully', 'success');
            setRevokingSessionId(null);
        },
        onError: (error) => {
            window.showToast?.(error.message || 'Failed to revoke session', 'error');
            setRevokingSessionId(null);
        }
    });

    const revokeAllSessions = useRevokeAllSessions({
        onSuccess: (data) => {
            window.showToast?.(`Signed out from ${data.revokedCount || 0} device(s)`, 'success');
            setShowRevokeAllConfirm(false);
        },
        onError: (error) => {
            window.showToast?.(error.message || 'Failed to sign out everywhere', 'error');
            setShowRevokeAllConfirm(false);
        }
    });

    const removeDevice = useRemoveDevice({
        onSuccess: () => {
            window.showToast?.('Device removed successfully', 'success');
            setRemovingDeviceId(null);
        },
        onError: (error) => {
            window.showToast?.(error.message || 'Failed to remove device', 'error');
            setRemovingDeviceId(null);
        }
    });

    const handleRevokeSession = (sessionId) => {
        setRevokingSessionId(sessionId);
        revokeSession.mutate(sessionId);
    };

    const handleRevokeAll = () => {
        revokeAllSessions.mutate();
    };

    const handleRemoveDevice = (deviceId) => {
        setRemovingDeviceId(deviceId);
        removeDevice.mutate(deviceId);
    };

    const sessions = sessionsData?.sessions || [];
    const devices = devicesData?.devices || [];
    const otherSessionsCount = sessions.filter(s => !s.isCurrent).length;

    return (
        <div className="space-y-6">
            {/* Active Sessions Section */}
            <Card className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                    <div>
                        <Heading level={3}>Active Sessions</Heading>
                        <Text variant="secondary" size="sm" className="mt-1">
                            Devices where you're currently signed in
                        </Text>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => refetchSessions()}
                            variant="ghost"
                            size="sm"
                        >
                            <Icon name="refresh" size="sm" className="mr-1" />
                            Refresh
                        </Button>
                        {otherSessionsCount > 0 && (
                            <Button
                                onClick={() => setShowRevokeAllConfirm(true)}
                                variant="secondary"
                                size="sm"
                                className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                            >
                                <Icon name="signOut" size="sm" className="mr-1" />
                                Sign Out Everywhere
                            </Button>
                        )}
                    </div>
                </div>

                {/* Sign Out Everywhere Confirmation */}
                {showRevokeAllConfirm && (
                    <Alert variant="warning" className="mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <Text size="sm" className="flex-1">
                                This will sign you out from all {otherSessionsCount} other device(s). Your current session will remain active.
                            </Text>
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleRevokeAll}
                                    disabled={revokeAllSessions.isPending}
                                    variant="danger"
                                    size="sm"
                                >
                                    {revokeAllSessions.isPending ? 'Signing out...' : 'Confirm'}
                                </Button>
                                <Button
                                    onClick={() => setShowRevokeAllConfirm(false)}
                                    disabled={revokeAllSessions.isPending}
                                    variant="ghost"
                                    size="sm"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </Alert>
                )}

                {sessionsLoading ? (
                    <div className="flex justify-center py-8">
                        <Spinner size="lg" />
                    </div>
                ) : sessions.length === 0 ? (
                    <Text variant="secondary" className="text-center py-8">
                        No active sessions found
                    </Text>
                ) : (
                    <div className="space-y-3">
                        {sessions.map((session) => (
                            <div
                                key={session.id}
                                className={`flex items-center justify-between p-4 rounded-lg border ${
                                    session.isCurrent
                                        ? 'bg-[#7B61FF]/10 border-[#7B61FF]/30'
                                        : 'bg-[#2A2D36] border-white/10'
                                }`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-lg ${session.isCurrent ? 'bg-[#7B61FF]/20' : 'bg-white/5'}`}>
                                        <Icon
                                            name={getDeviceIcon(session.device?.type)}
                                            size="lg"
                                            className={session.isCurrent ? 'text-[#7B61FF]' : 'text-gray-400'}
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Text weight="semibold">
                                                {session.device?.browser || 'Unknown Browser'} on {session.device?.os || 'Unknown OS'}
                                            </Text>
                                            {session.isCurrent && (
                                                <Badge variant="success" size="xs">This device</Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <Icon name="globe" size="xs" className="text-gray-500" />
                                            <Text variant="secondary" size="sm">
                                                {session.location?.city || 'Unknown'}, {session.location?.country || 'Unknown'}
                                            </Text>
                                            <span className="text-gray-600">•</span>
                                            <Text variant="secondary" size="sm">
                                                {formatRelativeTime(session.lastActivity)}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                                {!session.isCurrent && (
                                    <Button
                                        onClick={() => handleRevokeSession(session.id)}
                                        disabled={revokingSessionId === session.id}
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-400 hover:bg-red-500/10"
                                    >
                                        {revokingSessionId === session.id ? (
                                            <Spinner size="xs" />
                                        ) : (
                                            <Icon name="x" size="sm" />
                                        )}
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Remembered Devices Section */}
            <Card className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                    <div>
                        <Heading level={3}>Remembered Devices</Heading>
                        <Text variant="secondary" size="sm" className="mt-1">
                            Devices you've trusted for easier sign-in
                        </Text>
                    </div>
                    <Button
                        onClick={() => refetchDevices()}
                        variant="ghost"
                        size="sm"
                    >
                        <Icon name="refresh" size="sm" className="mr-1" />
                        Refresh
                    </Button>
                </div>

                {devicesLoading ? (
                    <div className="flex justify-center py-8">
                        <Spinner size="lg" />
                    </div>
                ) : devices.length === 0 ? (
                    <Text variant="secondary" className="text-center py-8">
                        No remembered devices
                    </Text>
                ) : (
                    <div className="space-y-3">
                        {devices.map((device) => (
                            <div
                                key={device.id}
                                className={`flex items-center justify-between p-4 rounded-lg border ${
                                    device.isCurrent
                                        ? 'bg-[#7B61FF]/10 border-[#7B61FF]/30'
                                        : 'bg-[#2A2D36] border-white/10'
                                }`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`p-2 rounded-lg ${device.isCurrent ? 'bg-[#7B61FF]/20' : 'bg-white/5'}`}>
                                        <Icon
                                            name={getDeviceIcon(device.type)}
                                            size="lg"
                                            className={device.isCurrent ? 'text-[#7B61FF]' : 'text-gray-400'}
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Text weight="semibold">{device.name}</Text>
                                            {device.isCurrent && (
                                                <Badge variant="success" size="xs">Current</Badge>
                                            )}
                                            {device.isTrusted && (
                                                <Badge variant="info" size="xs">Trusted</Badge>
                                            )}
                                            {device.isSuspicious && (
                                                <Badge variant="error" size="xs">Suspicious</Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <Icon name="globe" size="xs" className="text-gray-500" />
                                            <Text variant="secondary" size="sm">
                                                {device.location?.city || 'Unknown'}, {device.location?.country || 'Unknown'}
                                            </Text>
                                            <span className="text-gray-600">•</span>
                                            <Text variant="secondary" size="sm">
                                                Last seen {formatRelativeTime(device.lastSeen)}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => handleRemoveDevice(device.id)}
                                    disabled={removingDeviceId === device.id}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-400 hover:bg-red-500/10"
                                >
                                    {removingDeviceId === device.id ? (
                                        <Spinner size="xs" />
                                    ) : (
                                        'Remove'
                                    )}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}

export default SecurityTab;
